import React, { useEffect, useState } from 'react';
import { authenticateGate, verifyGateToken } from '../lib/accessGate';

/**
 * BrochureGate, password gate around private/internal pages.
 *
 * Architecture choices (so the password CANNOT be read from the bundle):
 *  • Password lives only in backend/.env (BROCHURE_PASSWORD).
 *  • Frontend POSTs the user-entered string to /api/access-gate/auth.
 *  • Backend returns an HMAC-signed token on success; frontend stores it
 *    in sessionStorage and revalidates against /api/access-gate/verify
 *    on every mount (so opening the same tab after token expiry
 *    re-prompts).
 *  • No hash, no salt, no client-side compare, anyone who downloads the
 *    JS bundle sees only the API call and the storage key. The actual
 *    secret never reaches the client.
 *
 * Reusable via props (defaults to brochure copy). Pass a unique
 * `tokenKey` if you want separate sessions for separate routes.
 */
const DEFAULT_TOKEN_KEY = 'kx.bro.tk';

export default function BrochureGate({
  children,
  title = 'This brochure is private.',
  subtitle = "Enter the access password you were given to view the full Keystone marketing brochure. If you don't have one, contact your CloudVoro point of contact.",
  buttonLabel = 'Unlock brochure',
  eyebrow = 'Keystone by CloudVoro · Confidential',
  tokenKey = DEFAULT_TOKEN_KEY,
  testIdPrefix = 'brochure',
}) {
  const [state, setState] = useState('checking'); // checking | gated | unlocked
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Revalidate any stored token against the backend on every mount.
  useEffect(() => {
    let cancelled = false;
    async function check() {
      const token = sessionStorage.getItem(tokenKey);
      if (!token) {
        if (!cancelled) setState('gated');
        return;
      }
      const valid = await verifyGateToken(token);
      if (cancelled) return;
      if (valid) {
        setState('unlocked');
      } else {
        sessionStorage.removeItem(tokenKey);
        setState('gated');
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { token } = await authenticateGate(password);
      sessionStorage.setItem(tokenKey, token);
      setState('unlocked');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setError('Incorrect password.');
      } else if (status === 503) {
        setError('Access is not configured. Please contact the team.');
      } else {
        setError('Network error. Please try again.');
      }
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  if (state === 'unlocked') {
    return children;
  }

  if (state === 'checking') {
    return (
      <div
        data-testid="brochure-gate-loading"
        style={{
          minHeight: '100vh',
          background: '#0A2540',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: 14,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        Verifying access…
      </div>
    );
  }

  return (
    <div
      data-testid="brochure-gate-form"
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(1200px 600px at 20% 20%, rgba(214, 51, 108, 0.18), transparent 60%), #0A2540',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 16px',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      <form
        onSubmit={handleSubmit}
        autoComplete="off"
        style={{
          width: '100%',
          maxWidth: 460,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.12)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          padding: '40px 36px 36px',
          borderRadius: 6,
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: '0.24em',
            textTransform: 'uppercase',
            color: '#FFC857',
            fontWeight: 700,
            marginBottom: 18,
          }}
        >
          {eyebrow}
        </div>
        <h1
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 30,
            fontWeight: 600,
            lineHeight: 1.15,
            letterSpacing: '-0.015em',
            margin: '0 0 12px',
            color: '#fff',
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.6,
            color: 'rgba(255,255,255,0.7)',
            margin: '0 0 28px',
          }}
        >
          {subtitle}
        </p>

        <label
          htmlFor="brochure-password"
          style={{
            display: 'block',
            fontSize: 11,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.55)',
            marginBottom: 8,
          }}
        >
          Access password
        </label>
        <input
          id="brochure-password"
          name="brochure-password"
          type="password"
          autoComplete="new-password"
          data-testid="brochure-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          required
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '14px 16px',
            background: 'rgba(0,0,0,0.25)',
            border: error
              ? '1px solid rgba(255, 80, 110, 0.7)'
              : '1px solid rgba(255,255,255,0.18)',
            borderRadius: 4,
            color: '#fff',
            fontSize: 15,
            fontFamily: 'inherit',
            outline: 'none',
            letterSpacing: '0.02em',
          }}
        />

        {error && (
          <div
            data-testid="brochure-gate-error"
            style={{
              marginTop: 12,
              fontSize: 13,
              color: '#FF93A8',
              lineHeight: 1.5,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !password}
          data-testid="brochure-password-submit"
          style={{
            marginTop: 22,
            width: '100%',
            padding: '14px 20px',
            background: submitting || !password ? 'rgba(214,51,108,0.4)' : '#D6336C',
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            border: 'none',
            borderRadius: 4,
            cursor: submitting || !password ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s ease, transform 0.05s ease',
          }}
        >
          {submitting ? 'Verifying…' : buttonLabel}
        </button>

        <div
          style={{
            marginTop: 24,
            paddingTop: 18,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            fontSize: 12,
            color: 'rgba(255,255,255,0.4)',
            lineHeight: 1.55,
          }}
        >
          Access is granted per recipient. Sessions are valid for 24 hours and
          tied to this browser tab.
        </div>
      </form>
    </div>
  );
}
